(() => {
  const EVIDENCE = [
    { id: 'love-stain', name: 'LOVE STAIN' },
    { id: 'chapel-key', name: 'CHAPEL KEY' },
    { id: 'vodka-cranberry', name: 'VODKA CRANBERRY' },
    { id: 'dirty-frequency', name: 'DIRTY FREQUENCY' },
    { id: 'forbidden-relic', name: 'FORBIDDEN RELIC' },
  ];

  const HINT_DELAY_SECONDS = 10;
  const MAX_HINTS = 2;
  const ACTION_GUARD_MS = 220;

  class GPEvidenceHunt extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.duration = this.numberFromData('duration', 45);
      this.wrongPenalty = this.numberFromData('wrongPenalty', 3);
      this.hintPenalty = this.numberFromData('hintPenalty', 5);
      this.productCount = this.numberFromData('productCount', 0);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.screens = [...this.querySelectorAll('[data-eh-screen]')];
      this.scene = this.querySelector('[data-eh-scene]');
      this.timerOutput = this.querySelector('[data-eh-timer]');
      this.foundOutput = this.querySelector('[data-eh-found]');
      this.statusOutput = this.querySelector('[data-eh-status]');
      this.hintButton = this.querySelector('[data-eh-hint]');
      this.hintsLeftOutput = this.querySelector('[data-eh-hints-left]');
      this.tray = this.querySelector('[data-eh-tray]');
      this.announcer = this.querySelector('[data-eh-announcer]');
      this.missRipple = this.querySelector('[data-eh-miss]');
      this.scanner = this.querySelector('[data-eh-scanner]');
      this.targets = [...this.querySelectorAll('[data-evidence]')];
      this.decoys = [...this.querySelectorAll('[data-eh-decoy]')];
      this.startButtons = [...this.querySelectorAll('[data-eh-start]')];
      this.retryButtons = [...this.querySelectorAll('[data-eh-retry]')];
      this.downloadButton = this.querySelector('[data-eh-download]');
      this.shopButton = this.querySelector('[data-eh-shop]');
      this.tickFrame = 0;
      this.scannerFrame = 0;
      this.scannerPoint = { x: 0, y: 0 };

      this.startButtons.forEach((button) => button.addEventListener('click', () => this.startHunt(false)));
      this.retryButtons.forEach((button) => button.addEventListener('click', () => this.startHunt(true)));
      this.targets.forEach((target) => target.addEventListener('click', (event) => this.inspectEvidence(event, target)));
      this.decoys.forEach((decoy) => decoy.addEventListener('click', (event) => this.inspectDecoy(event)));
      this.hintButton?.addEventListener('click', () => this.runHint());
      this.downloadButton?.addEventListener('click', () => this.downloadCaseFile());
      this.shopButton?.addEventListener('click', () => this.shopEvidence());
      this.addEventListener('keydown', this.handleKeydown);
      this.scene?.addEventListener('pointerenter', this.handlePointerEnter);
      this.scene?.addEventListener('pointermove', this.handlePointerMove, { passive: true });
      this.scene?.addEventListener('pointerleave', this.handlePointerLeave);
      this.scene?.addEventListener('touchmove', this.preventActiveSceneScroll, { passive: false });
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      this.resetState();
      this.showScreen('briefing', false);
    }

    disconnectedCallback() {
      window.cancelAnimationFrame(this.tickFrame);
      window.cancelAnimationFrame(this.scannerFrame);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    numberFromData(key, fallback) {
      const value = Number(this.dataset[key]);
      return Number.isFinite(value) ? value : fallback;
    }

    resetState() {
      window.cancelAnimationFrame(this.tickFrame);
      window.cancelAnimationFrame(this.scannerFrame);
      window.clearTimeout(this.statusTimer);
      window.clearTimeout(this.hintTimer);

      this.state = 'briefing';
      this.startedAt = 0;
      this.penaltySeconds = 0;
      this.remaining = this.duration;
      this.found = new Set();
      this.falseLeads = 0;
      this.hintsUsed = 0;
      this.lastActionAt = -Infinity;
      this.lastAnnouncedSecond = null;
      this.detectiveCode = '';

      if (this.timerOutput) this.timerOutput.textContent = String(this.duration);
      if (this.foundOutput) this.foundOutput.textContent = '0';
      if (this.hintsLeftOutput) this.hintsLeftOutput.textContent = `[${MAX_HINTS}]`;
      if (this.hintButton) this.hintButton.disabled = true;
      if (this.statusOutput) {
        this.statusOutput.textContent = 'SCENE LIVE // LOCATE EVIDENCE';
        this.statusOutput.classList.remove('is-alert');
      }

      this.targets.forEach((target) => {
        target.disabled = false;
        target.classList.remove('is-found', 'is-hinted');
        target.setAttribute('aria-label', 'Inspect suspicious object');
      });
      this.decoys.forEach((decoy) => { decoy.disabled = false; });
      this.tray?.querySelectorAll('[data-tray-item]').forEach((item) => item.classList.remove('is-secured'));
      this.scene?.classList.remove('is-active', 'is-scanning');
    }

    startHunt(isRetry) {
      this.resetState();
      this.state = 'active';
      this.startedAt = window.performance.now();
      this.scene?.classList.add('is-active');
      this.showScreen('hunt');
      this.announce(`${isRetry ? 'Case reset. ' : ''}Evidence hunt started. ${this.duration} seconds remaining. Five pieces of evidence missing.`);
      this.emit('gp:evidence-hunt:start', { duration: this.duration, retry: Boolean(isRetry) });
      this.tickFrame = window.requestAnimationFrame(this.tick);
      window.requestAnimationFrame(() => this.targets[0]?.focus({ preventScroll: true }));
    }

    tick = (now) => {
      if (this.state !== 'active') return;
      this.updateRemaining(now);
      if (this.remaining <= 0) {
        this.finishCold();
        return;
      }

      const elapsed = this.elapsedSeconds(now);
      if (this.hintButton && elapsed >= HINT_DELAY_SECONDS && this.hintsUsed < MAX_HINTS) {
        this.hintButton.disabled = false;
      }

      const shownSecond = Math.ceil(this.remaining);
      if (this.timerOutput) this.timerOutput.textContent = String(shownSecond);
      if (shownSecond !== this.lastAnnouncedSecond && (shownSecond <= 5 || shownSecond % 10 === 0)) {
        this.lastAnnouncedSecond = shownSecond;
        this.announce(`${shownSecond} seconds remaining.`);
      }
      this.tickFrame = window.requestAnimationFrame(this.tick);
    };

    elapsedSeconds(now = window.performance.now()) {
      return Math.max(0, (now - this.startedAt) / 1000);
    }

    updateRemaining(now = window.performance.now()) {
      this.remaining = Math.max(0, this.duration - this.elapsedSeconds(now) - this.penaltySeconds);
      if (this.timerOutput) this.timerOutput.textContent = String(Math.ceil(this.remaining));
      return this.remaining;
    }

    actionAllowed() {
      if (this.state !== 'active') return false;
      const now = window.performance.now();
      if (now - this.lastActionAt < ACTION_GUARD_MS) return false;
      this.lastActionAt = now;
      return true;
    }

    inspectEvidence(event, target) {
      if (!this.actionAllowed()) return;
      const evidenceId = target.dataset.evidence;
      if (!evidenceId || this.found.has(evidenceId)) return;

      this.found.add(evidenceId);
      target.disabled = true;
      target.classList.add('is-found');
      target.setAttribute('aria-label', `${target.dataset.name} secured`);
      this.tray?.querySelector(`[data-tray-item="${evidenceId}"]`)?.classList.add('is-secured');
      if (this.foundOutput) this.foundOutput.textContent = String(this.found.size);

      const evidenceNumber = String(EVIDENCE.findIndex((item) => item.id === evidenceId) + 1).padStart(2, '0');
      this.setStatus(`EVIDENCE ${evidenceNumber} SECURED`, false, 1500);
      this.announce(`${target.dataset.name} secured. Evidence ${this.found.size} of ${EVIDENCE.length}. ${Math.ceil(this.remaining)} seconds remaining.`);
      this.emit('gp:evidence-hunt:found', { evidenceId, found: this.found.size, remaining: Math.ceil(this.remaining) });

      if (this.found.size === EVIDENCE.length) {
        this.finishSolved();
      } else {
        const nextTarget = this.targets.find((candidate) => !candidate.disabled);
        if (event.detail === 0) window.requestAnimationFrame(() => nextTarget?.focus({ preventScroll: true }));
      }
    }

    inspectDecoy(event) {
      if (!this.actionAllowed()) return;
      this.falseLeads += 1;
      this.penaltySeconds += this.wrongPenalty;
      this.updateRemaining();
      this.showMissRipple(event);
      this.setStatus(`FALSE LEAD // -${this.wrongPenalty} SECONDS`, true, 1100);
      this.announce(`False lead. ${this.wrongPenalty} second penalty. ${Math.ceil(this.remaining)} seconds remaining.`);
      this.emit('gp:evidence-hunt:false-lead', { falseLeads: this.falseLeads, remaining: Math.ceil(this.remaining) });
      if (this.remaining <= 0) this.finishCold();
    }

    showMissRipple(event) {
      if (!this.missRipple || !this.scene) return;
      const sceneRect = this.scene.getBoundingClientRect();
      const targetRect = event.currentTarget.getBoundingClientRect();
      const clientX = event.clientX || targetRect.left + targetRect.width / 2;
      const clientY = event.clientY || targetRect.top + targetRect.height / 2;
      this.missRipple.style.left = `${clientX - sceneRect.left}px`;
      this.missRipple.style.top = `${clientY - sceneRect.top}px`;
      this.missRipple.classList.remove('is-visible');
      void this.missRipple.offsetWidth;
      this.missRipple.classList.add('is-visible');
    }

    runHint() {
      if (!this.actionAllowed() || this.hintsUsed >= MAX_HINTS) return;
      if (this.elapsedSeconds() < HINT_DELAY_SECONDS) return;
      const candidates = this.targets.filter((target) => !this.found.has(target.dataset.evidence));
      if (!candidates.length) return;

      this.hintsUsed += 1;
      this.penaltySeconds += this.hintPenalty;
      this.updateRemaining();
      const target = candidates[Math.floor(Math.random() * candidates.length)];
      target.classList.add('is-hinted');
      window.clearTimeout(this.hintTimer);
      this.hintTimer = window.setTimeout(() => target.classList.remove('is-hinted'), this.reducedMotion ? 120 : 1050);
      if (this.hintsLeftOutput) this.hintsLeftOutput.textContent = `[${MAX_HINTS - this.hintsUsed}]`;
      if (this.hintButton) this.hintButton.disabled = this.hintsUsed >= MAX_HINTS;
      this.setStatus(`SCENE SCAN // -${this.hintPenalty} SECONDS`, false, 1200);
      this.announce(`Scene scan located an unfound object. ${this.hintPenalty} second penalty. ${Math.ceil(this.remaining)} seconds remaining.`);
      this.emit('gp:evidence-hunt:hint', { hintsUsed: this.hintsUsed, remaining: Math.ceil(this.remaining) });
      if (this.remaining <= 0) this.finishCold();
    }

    setStatus(message, isAlert, duration) {
      if (!this.statusOutput) return;
      window.clearTimeout(this.statusTimer);
      this.statusOutput.textContent = message;
      this.statusOutput.classList.toggle('is-alert', isAlert);
      this.statusTimer = window.setTimeout(() => {
        if (this.state !== 'active') return;
        this.statusOutput.textContent = 'SCENE LIVE // LOCATE EVIDENCE';
        this.statusOutput.classList.remove('is-alert');
      }, duration);
    }

    finishSolved() {
      if (this.state !== 'active') return;
      this.updateRemaining();
      this.state = 'solved';
      window.cancelAnimationFrame(this.tickFrame);
      this.scene?.classList.remove('is-active', 'is-scanning');
      this.targets.forEach((target) => { target.disabled = true; });
      this.decoys.forEach((decoy) => { decoy.disabled = true; });

      const wallTime = this.elapsedSeconds();
      const remainingSeconds = Math.max(0, Math.floor(this.remaining));
      const classification = this.classificationFor(remainingSeconds);
      this.detectiveCode = this.createDetectiveCode();
      this.querySelector('[data-eh-completion]').textContent = this.formatTime(wallTime);
      this.querySelector('[data-eh-remaining]').textContent = `${remainingSeconds} SECONDS`;
      this.querySelector('[data-eh-false-leads]').textContent = String(this.falseLeads);
      this.querySelector('[data-eh-classification]').textContent = classification;
      this.showScreen('solved');
      this.announce(`Case solved. All evidence recovered. Classification: ${classification}. ${remainingSeconds} seconds remaining.`);
      this.emit('gp:evidence-hunt:solved', { classification, remaining: remainingSeconds, falseLeads: this.falseLeads });
    }

    finishCold() {
      if (this.state !== 'active') return;
      this.state = 'cold';
      this.remaining = 0;
      if (this.timerOutput) this.timerOutput.textContent = '0';
      window.cancelAnimationFrame(this.tickFrame);
      this.scene?.classList.remove('is-active', 'is-scanning');
      this.targets.forEach((target) => { target.disabled = true; });
      this.decoys.forEach((decoy) => { decoy.disabled = true; });
      this.querySelector('[data-eh-cold-found]').textContent = String(this.found.size);
      this.querySelector('[data-eh-cold-missing]').textContent = String(EVIDENCE.length - this.found.size);
      this.querySelector('[data-eh-cold-false]').textContent = String(this.falseLeads);
      this.showScreen('cold');
      this.announce(`Case cold. Evidence was compromised. ${this.found.size} recovered. ${EVIDENCE.length - this.found.size} missing.`);
      this.emit('gp:evidence-hunt:cold', { found: this.found.size, missing: EVIDENCE.length - this.found.size, falseLeads: this.falseLeads });
    }

    classificationFor(remainingSeconds) {
      if (remainingSeconds >= 35) return 'MASTER DETECTIVE';
      if (remainingSeconds >= 20) return 'CASE BREAKER';
      return 'EVIDENCE RECOVERED';
    }

    formatTime(seconds) {
      const wholeSeconds = Math.max(0, Math.round(seconds));
      const minutes = Math.floor(wholeSeconds / 60);
      return `${String(minutes).padStart(2, '0')}:${String(wholeSeconds % 60).padStart(2, '0')}`;
    }

    createDetectiveCode() {
      const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
      let output = '';
      try {
        const values = new Uint8Array(6);
        window.crypto.getRandomValues(values);
        values.forEach((value) => { output += alphabet[value % alphabet.length]; });
      } catch (_error) {
        for (let index = 0; index < 6; index += 1) output += alphabet[Math.floor(Math.random() * alphabet.length)];
      }
      return `GP-${output}`;
    }

    downloadCaseFile() {
      if (this.state !== 'solved') return;
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1350;
      const context = canvas.getContext('2d');
      if (!context) {
        this.announce('Case file download is unavailable in this browser.');
        return;
      }

      const classification = this.querySelector('[data-eh-classification]').textContent;
      const completionTime = this.querySelector('[data-eh-completion]').textContent;
      context.fillStyle = '#060606';
      context.fillRect(0, 0, 1080, 1350);
      const glow = context.createRadialGradient(810, 250, 10, 810, 250, 650);
      glow.addColorStop(0, 'rgba(160,22,22,.34)');
      glow.addColorStop(1, 'rgba(160,22,22,0)');
      context.fillStyle = glow;
      context.fillRect(0, 0, 1080, 1350);

      context.strokeStyle = 'rgba(242,232,201,.26)';
      context.lineWidth = 2;
      context.strokeRect(54, 54, 972, 1242);
      context.strokeRect(70, 70, 940, 1210);
      context.fillStyle = '#a01616';
      context.fillRect(70, 70, 940, 12);

      context.textAlign = 'left';
      context.fillStyle = '#e12626';
      context.font = '700 28px Arial';
      context.fillText('GUILTY PARTY / FIELD UNIT', 108, 145);
      context.fillStyle = '#f2e8c9';
      context.font = '900 104px Impact, Arial Black, sans-serif';
      context.fillText('EVIDENCE', 104, 280);
      context.fillText('HUNT', 104, 382);

      context.fillStyle = '#e12626';
      context.font = '900 58px Arial Black, Arial, sans-serif';
      context.fillText('CASE SOLVED', 108, 492);
      context.font = '700 25px Arial';
      context.fillText('ALL EVIDENCE RECOVERED', 110, 535);

      context.strokeStyle = 'rgba(242,232,201,.3)';
      context.beginPath();
      context.moveTo(108, 588);
      context.lineTo(972, 588);
      context.stroke();

      const rows = [
        ['PUBLIC DETECTIVE CODE', this.detectiveCode],
        ['CLASSIFICATION', classification],
        ['COMPLETION TIME', completionTime],
      ];
      rows.forEach(([label, value], index) => {
        const y = 652 + index * 92;
        context.fillStyle = 'rgba(242,232,201,.52)';
        context.font = '700 19px Arial';
        context.fillText(label, 110, y);
        context.fillStyle = '#f2e8c9';
        context.font = '900 34px Arial Black, Arial, sans-serif';
        context.fillText(value, 110, y + 42);
      });

      context.fillStyle = '#e12626';
      context.font = '800 20px Arial';
      context.fillText('RECOVERED EVIDENCE', 110, 936);
      EVIDENCE.forEach((item, index) => {
        const y = 990 + index * 48;
        context.fillStyle = '#a01616';
        context.fillRect(110, y - 22, 29, 29);
        context.fillStyle = '#fff7df';
        context.font = '800 14px Arial';
        context.fillText(String(index + 1).padStart(2, '0'), 116, y - 2);
        context.fillStyle = '#f2e8c9';
        context.font = '800 24px Arial';
        context.fillText(item.name, 160, y);
      });

      context.save();
      context.translate(820, 1100);
      context.rotate(-0.1);
      context.strokeStyle = '#e12626';
      context.lineWidth = 8;
      context.strokeRect(-145, -58, 290, 116);
      context.strokeRect(-134, -47, 268, 94);
      context.fillStyle = '#e12626';
      context.textAlign = 'center';
      context.font = '900 31px Arial Black, Arial, sans-serif';
      context.fillText('EVIDENCE', 0, -5);
      context.fillText('SECURED', 0, 34);
      context.restore();

      context.textAlign = 'left';
      context.fillStyle = 'rgba(242,232,201,.7)';
      context.font = '700 24px Arial';
      context.fillText('guiltyparty.in', 110, 1250);
      context.textAlign = 'right';
      context.fillText('CASE 001 // NIGHT SHIFT', 970, 1250);

      const link = document.createElement('a');
      link.download = `guilty-party-evidence-hunt-${this.detectiveCode.toLowerCase()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      this.announce('Case file downloaded as a 1080 by 1350 PNG.');
      this.emit('gp:evidence-hunt:download', { detectiveCode: this.detectiveCode });
    }

    shopEvidence() {
      const products = this.querySelector('[data-eh-products]');
      if (products && this.productCount > 0) {
        products.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'start' });
        window.requestAnimationFrame(() => products.querySelector('a[href]')?.focus({ preventScroll: true }));
        this.announce('Recovered evidence products displayed.');
        return;
      }
      window.location.href = this.dataset.marketplaceUrl || '/collections/all';
    }

    showScreen(screenName, moveFocus = true) {
      this.screens.forEach((screen) => {
        screen.hidden = screen.dataset.ehScreen !== screenName;
      });
      if (moveFocus && screenName !== 'hunt') {
        const heading = this.querySelector(`[data-eh-screen="${screenName}"] h2, [data-eh-screen="${screenName}"] h1`);
        window.requestAnimationFrame(() => heading?.focus({ preventScroll: true }));
      }
      if (moveFocus) this.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'start' });
    }

    announce(message) {
      if (!this.announcer) return;
      this.announcer.textContent = '';
      window.setTimeout(() => { this.announcer.textContent = message; }, 25);
    }

    emit(eventName, detail) {
      window.dispatchEvent(new CustomEvent(eventName, { detail }));
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ event: eventName.replaceAll(':', '_'), ...detail });
      }
    }

    preventActiveSceneScroll = (event) => {
      if (this.state === 'active') event.preventDefault();
    };

    handleKeydown = (event) => {
      if (event.defaultPrevented || event.repeat || (event.key !== 'Enter' && event.key !== ' ')) return;
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || button.disabled) return;
      event.preventDefault();
      button.click();
    };

    handlePointerEnter = (event) => {
      if (this.state !== 'active' || event.pointerType === 'touch') return;
      this.scene?.classList.add('is-scanning');
      this.handlePointerMove(event);
    };

    handlePointerMove = (event) => {
      if (this.state !== 'active' || event.pointerType === 'touch' || !this.scene) return;
      this.scene.classList.add('is-scanning');
      const rect = this.scene.getBoundingClientRect();
      this.scannerPoint.x = event.clientX - rect.left;
      this.scannerPoint.y = event.clientY - rect.top;
      if (!this.scannerFrame) this.scannerFrame = window.requestAnimationFrame(this.renderScanner);
    };

    renderScanner = () => {
      this.scannerFrame = 0;
      if (!this.scanner || this.state !== 'active') return;
      this.scanner.style.transform = `translate3d(${this.scannerPoint.x}px, ${this.scannerPoint.y}px, 0)`;
    };

    handlePointerLeave = () => {
      this.scene?.classList.remove('is-scanning');
      window.cancelAnimationFrame(this.scannerFrame);
      this.scannerFrame = 0;
    };

    handleVisibilityChange = () => {
      if (!document.hidden && this.state === 'active') {
        this.updateRemaining();
        if (this.remaining <= 0) this.finishCold();
      }
    };
  }

  if (!customElements.get('gp-evidence-hunt')) {
    customElements.define('gp-evidence-hunt', GPEvidenceHunt);
  }
})();
